import Sliders from './form/sliders.js';
import OptionLists from './form/option-lists.js';
import OptionSelects from './form/option-selects.js';
import SwitchToggles from './form/switch-toggles.js';
import ButtonGroups from './form/btn-groups.js';

const EVENT_TYPE = 'input';
const EVENT = new Event(EVENT_TYPE, { bubbles: true });

class Form {
	#container;
	#form;
	#formKeys = [];
	#sliders;
	#options;
	#selects;
	#toggles;
	#btnGroups;
	#elementKeys = [ 'sliders', 'options', 'selects', 'toggles', 'btnGroups' ];

	constructor(container) {
		if (container instanceof jQuery) {
			container = container[0];
		}

		this.#container = container;
		this.#form = container.matches('form') ? container : container.querySelector('form');

		this.#init();
	}

	#init() {
		if (this.#container.querySelector('.option-list')) {
			this.#options = OptionLists.init(this.#container);
		}

		if (this.#container.querySelector('.option-select')) {
			this.#selects = OptionSelects.init(this.#container);
		}

		if (this.#container.querySelector('.slider')) {
			this.#sliders = Sliders.init(this.#container);
		}

		if (this.#container.querySelector('.switch-toggle')) {
			this.#toggles = SwitchToggles.init(this.#container);
		}

		if (this.#container.querySelector('.btn-group[data-name]')) {
			this.#btnGroups = ButtonGroups.init(this.#container);
		}

		this.#formKeys = this.#getFormKeys();
	}

	#getElementProperty(key) {
		if (key === 'options') {
			return this.#options;
		}

		if (key === 'selects') {
			return this.#selects;
		}

		if (key === 'sliders') {
			return this.#sliders;
		}

		if (key === 'toggles') {
			return this.#toggles;
		}

		if (key === 'btnGroups') {
			return this.#btnGroups;
		}

		return undefined;
	}

	#getElement(name) {
		for (const key of this.#elementKeys) {
			if (this.#getElementProperty(key)?.has(name)) {
				return this.#getElementProperty(key);
			}
		}

		return this.#form.elements[ name ];
	}

	get(name) {
		const el = this.#getElement(name);

		if (el instanceof Element || el instanceof RadioNodeList) {
			const value = el.type === 'checkbox' ? (el.checked ? el.value : 0) : el.value;

			return value;
		} else if (el) {
			return el.get(name);
		}
	}

	has(name) {
		for (const key of this.#elementKeys) {
			if (this.#getElementProperty(key)?.has(name)) {
				return true;
			}
		}

		return !!this.#form.elements[ name ];
	}

	set(name, value, ... args) {
		const el = this.#getElement(name);

		if (el instanceof Element || el instanceof RadioNodeList) {
			const changed = el.value !== value.toString();

			el.value = value;

			if (el instanceof RadioNodeList) {
				for (const radio of [ ... el ]) {
					if (radio.checked) {
						radio.dispatchEvent(EVENT);
					}
				}
			} else {
				el.dispatchEvent(EVENT);
			}

			return changed;
		} else if (el) {
			return el.set(name, value, ... args);
		}
	}

	update(name, value) {
		const el = this.#getElement(name);

		if (el instanceof Element || el instanceof RadioNodeList) {
			const changed = el.value !== value.toString();

			el.value = value;

			return changed;
		} else if (el) {
			return el.update(name, value);
		}
	}

	reset(name = null) {
		if (!name) {
			for (const key of this.#elementKeys) {
				if (this.#getElementProperty(key)) {
					this.#getElementProperty(key).reset();
				}
			}

			return;
		}

		const el = this.#getElement(name);

		if (el instanceof Element || el instanceof RadioNodeList) {
			if (el instanceof RadioNodeList) {
				for (const radio of [ ... el ]) {
					radio.reset();
				}
			} else {
				el.reset();
			}
		} else if (el) {
			el.reset(name);
		}
	}

	#elementsData() {
		let elements = {};

		for (const key of this.#elementKeys) {
			if (this.#getElementProperty(key)) {
				elements = {
					... elements,
					... (this.#getElementProperty(key) ? this.#getElementProperty(key).data() : {}),
				};
			}
		}

		return elements;
	}

	#getFormKeys() {
		const data = this.#elementsData();

		const formData = new FormData(this.#form);
		const entries = [ ... formData.entries() ].filter(([ key, value ]) => !data.hasOwnProperty(key));
		const keys = _.uniq(entries.map(entry => entry[0]));

		return keys;
	}

	data() {
		const data = this.#elementsData();
		const formData = new FormData(this.#form);

		for (const key of this.#formKeys) {
			data[ key ] = formData.get(key);
		}

		return data;
	}

	setCallback(name, type, callback) {
		const el = this.#getElement(name);

		if (el instanceof Element || el instanceof RadioNodeList) {
			this.#form.addEventListener(EVENT_TYPE, ({ target }) => {
				if (target.name !== name) {
					return;
				}

				callback(target.type === 'checkbox' ? (target.checked ? target.value : 0) : target.value);
			});
		} else if (el) {
			el.setCallback(name, type, callback);
		}
	}

	setGlobalCallback(type, callback) {
		for (const key of this.#elementKeys) {
			if (this.#getElementProperty(key)) {
				this.#getElementProperty(key).setGlobalCallback(type, callback);
			}
		}

		for (const key of this.#formKeys) {
			this.setCallback(key, type, callback);
		}
	}
}

function init(container) {
	return new Form(container);
}

export default { init }
